import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@cargomatic/ui';
import { addHubSchema } from './schemas';
import { useYards } from './hooks/useYards';
import { ApolloProvider } from '@apollo/client';
import { YardClient } from 'utils/YardAPIUtils';
export const AddHubModal = ({ isOpen, onClose, onSubmit, }) => {
    // Provider needs to be above any hook usage; we delegate to a child component
    return (React.createElement(ApolloProvider, { client: YardClient },
        React.createElement(AddHubModalContent, { isOpen: isOpen, onClose: onClose, onSubmit: onSubmit })));
};
function AddHubModalContent({ isOpen, onClose, onSubmit }) {
    const { yards, isLoading: loadingYards } = useYards({ isOpen });
    const { handleSubmit, watch, setValue, reset, formState: { errors }, } = useForm({
        resolver: zodResolver(addHubSchema),
        defaultValues: {
            hubbingType: 'Pre-Pull',
            yardId: '',
        },
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    // Reset when modal closes
    React.useEffect(() => {
        if (!isOpen) {
            reset({ yardId: '', hubbingType: 'Pre-Pull' });
        }
    }, [isOpen, reset]);
    const handleFormSubmit = async (data) => {
        try {
            setIsSubmitting(true);
            await onSubmit(data);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    return (React.createElement(Dialog, { open: isOpen, onOpenChange: onClose },
        React.createElement(DialogContent, { className: "max-w-md max-h-[85vh] overflow-y-auto" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Add Hub"),
                React.createElement(DialogDescription, null, "Add a yard location. This will split the shipment into two legs.")),
            React.createElement("form", { onSubmit: handleSubmit(handleFormSubmit), className: "grid gap-4 py-4" },
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "yardId" }, "Yard"),
                    React.createElement(Select, { value: watch('yardId'), onValueChange: (value) => setValue('yardId', value), disabled: loadingYards || isSubmitting },
                        React.createElement(SelectTrigger, { id: "yardId", className: "w-full" },
                            React.createElement(SelectValue, { placeholder: loadingYards ? 'Loading yards...' : 'Select a yard' })),
                        React.createElement(SelectContent, null, yards.map((y) => {
                            const label = `${y.nameInternal || y.id}${y.address ? ` — ${y.address}` : ''}`;
                            return (React.createElement(SelectItem, { key: y.id, value: y.id }, label));
                        }))),
                    errors.yardId && (React.createElement("p", { className: "text-xs text-red-500" }, errors.yardId.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "hubbingType" }, "Hub Type"),
                    React.createElement(Select, { value: watch('hubbingType'), onValueChange: (value) => setValue('hubbingType', value), disabled: isSubmitting },
                        React.createElement(SelectTrigger, { id: "hubbingType", className: "w-full" },
                            React.createElement(SelectValue, { placeholder: "Select hub type" })),
                        React.createElement(SelectContent, null,
                            React.createElement(SelectItem, { value: "Pre-Pull" }, "Pre-Pull"),
                            React.createElement(SelectItem, { value: "Stop-Off" }, "Stop-Off"),
                            React.createElement(SelectItem, { value: "Yard-Diversion" }, "Yard Diversion"))),
                    errors.hubbingType && (React.createElement("p", { className: "text-xs text-red-500" }, errors.hubbingType.message))),
                React.createElement(DialogFooter, null,
                    React.createElement(Button, { type: "button", variant: "outline", onClick: onClose, disabled: isSubmitting }, "Cancel"),
                    React.createElement(Button, { type: "submit", disabled: isSubmitting }, isSubmitting ? 'Adding hub...' : 'Add Hub'))))));
}
